import { useEffect, useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import {
  BufferAttribute,
  BufferGeometry,
  DynamicDrawUsage,
  ShaderMaterial,
} from 'three';
import type { MeshBasicMaterial } from 'three';
import type { RefObject } from 'react';
import { localToWorld, type VehicleState } from '../model/vehicleDriving';
import { sampleDrivingTerrain } from '../model/drivingTerrain';
import { DRIVING_SURFACE_TYPES } from '../model/drivingSurface';
import type { CarBounds } from '../types';
import type { RuntimeWeather } from '../model/weatherTransition';

const PARTICLE_COUNT = 80;
const PARTICLES_PER_SECOND = 34;
const PARTICLE_HIDE_Y = -10_000;
const SKID_SEGMENT_COUNT = 768;
const EFFECT_STEP_LIMIT = 1 / 20;
const MIN_EFFECT_SPEED = 11;
/** 연기/물보라는 큰 슬립에서만 보여 화면을 가리지 않습니다. */
const MIN_SMOKE_SLIP = 0.2;
/** 마른 노면 급제동도 약 65~70km/h부터 자국을 남기도록 별도 임계값을 둡니다. */
const MIN_SKID_SLIP = 0.1;
const MAX_CONNECTED_DISTANCE = 5;
const TYRE_MARK_WIDTH = 0.2;
const MARK_Y_OFFSET = 0.007;

const SMOKE_VERTEX_SHADER = `
  attribute float aSize;
  attribute float aOpacity;
  attribute float aSeed;
  attribute vec3 aColor;
  varying float vOpacity;
  varying float vSeed;
  varying vec3 vColor;

  void main() {
    vec4 viewPosition = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * viewPosition;
    gl_PointSize = aSize * (280.0 / max(1.0, -viewPosition.z));
    vOpacity = aOpacity;
    vSeed = aSeed;
    vColor = aColor;
  }
`;

const SMOKE_FRAGMENT_SHADER = `
  varying float vOpacity;
  varying float vSeed;
  varying vec3 vColor;

  void main() {
    vec2 centered = gl_PointCoord - vec2(0.5);
    float distanceFromCenter = length(centered) * 2.0;
    if (distanceFromCenter >= 1.0) discard;

    float softEdge = 1.0 - smoothstep(0.08, 1.0, distanceFromCenter);
    float breakup = 0.88 + 0.12 * sin(
      centered.x * 11.0 + centered.y * 8.0 + vSeed * 6.2831853
    );
    float alpha = vOpacity * softEdge * softEdge * breakup;
    if (alpha < 0.004) discard;
    gl_FragColor = vec4(vColor, alpha);
  }
`;

interface ParticlePool {
  geometry: BufferGeometry;
  positions: Float32Array;
  velocities: Float32Array;
  life: Float32Array;
  maxLife: Float32Array;
  baseSize: Float32Array;
  sizes: Float32Array;
  opacities: Float32Array;
  colors: Float32Array;
  cursor: number;
  emissionRemainder: number;
}

interface SkidPool {
  geometry: BufferGeometry;
  positions: Float32Array;
  cursor: number;
}

interface ContactPoint {
  x: number;
  y: number;
  z: number;
}

interface PreviousContacts {
  left: ContactPoint | null;
  right: ContactPoint | null;
  spawnCount: number;
}

function dynamicAttribute(array: Float32Array, itemSize: number): BufferAttribute {
  const attribute = new BufferAttribute(array, itemSize);
  attribute.setUsage(DynamicDrawUsage);
  return attribute;
}

function createParticlePool(): ParticlePool {
  const geometry = new BufferGeometry();
  const positions = new Float32Array(PARTICLE_COUNT * 3);
  const velocities = new Float32Array(PARTICLE_COUNT * 3);
  const life = new Float32Array(PARTICLE_COUNT);
  const maxLife = new Float32Array(PARTICLE_COUNT);
  const baseSize = new Float32Array(PARTICLE_COUNT);
  const sizes = new Float32Array(PARTICLE_COUNT);
  const opacities = new Float32Array(PARTICLE_COUNT);
  const colors = new Float32Array(PARTICLE_COUNT * 3);
  const seeds = Float32Array.from({ length: PARTICLE_COUNT }, () => Math.random());

  for (let index = 0; index < PARTICLE_COUNT; index += 1) {
    positions[index * 3 + 1] = PARTICLE_HIDE_Y;
  }
  geometry.setAttribute('position', dynamicAttribute(positions, 3));
  geometry.setAttribute('aSize', dynamicAttribute(sizes, 1));
  geometry.setAttribute('aOpacity', dynamicAttribute(opacities, 1));
  geometry.setAttribute('aColor', dynamicAttribute(colors, 3));
  geometry.setAttribute('aSeed', new BufferAttribute(seeds, 1));
  return {
    geometry,
    positions,
    velocities,
    life,
    maxLife,
    baseSize,
    sizes,
    opacities,
    colors,
    cursor: 0,
    emissionRemainder: 0,
  };
}

function createSkidPool(): SkidPool {
  const geometry = new BufferGeometry();
  const positions = new Float32Array(SKID_SEGMENT_COUNT * 6 * 3);
  for (let index = 0; index < SKID_SEGMENT_COUNT * 6; index += 1) {
    positions[index * 3 + 1] = PARTICLE_HIDE_Y;
  }
  geometry.setAttribute('position', dynamicAttribute(positions, 3));
  return { geometry, positions, cursor: 0 };
}

function createSmokeMaterial(): ShaderMaterial {
  return new ShaderMaterial({
    vertexShader: SMOKE_VERTEX_SHADER,
    fragmentShader: SMOKE_FRAGMENT_SHADER,
    transparent: true,
    depthWrite: false,
  });
}

function tyreContact(state: VehicleState, localX: number, localZ: number): ContactPoint {
  const world = localToWorld(state, localX, localZ);
  const terrain = sampleDrivingTerrain(world.x, world.z);
  return { x: world.x, y: terrain.height + MARK_Y_OFFSET, z: world.z };
}

function distanceBetween(first: ContactPoint, second: ContactPoint): number {
  return Math.hypot(first.x - second.x, first.y - second.y, first.z - second.z);
}

function writeVertex(
  positions: Float32Array,
  offset: number,
  point: ContactPoint,
  offsetX: number,
  offsetZ: number
): number {
  positions[offset] = point.x + offsetX;
  positions[offset + 1] = point.y;
  positions[offset + 2] = point.z + offsetZ;
  return offset + 3;
}

/** 선분 대신 두 삼각형으로 된 타이어 폭의 사각 리본 한 조각을 기록합니다. */
function addSkidSegment(pool: SkidPool, start: ContactPoint, end: ContactPoint): void {
  const distance = distanceBetween(start, end);
  if (distance < 0.025 || distance > MAX_CONNECTED_DISTANCE) {
    return;
  }

  const dx = end.x - start.x;
  const dz = end.z - start.z;
  const horizontalLength = Math.hypot(dx, dz);
  if (horizontalLength < 0.001) {
    return;
  }
  const halfWidth = TYRE_MARK_WIDTH / 2;
  const sideX = (-dz / horizontalLength) * halfWidth;
  const sideZ = (dx / horizontalLength) * halfWidth;
  let offset = pool.cursor * 18;

  // start-left, start-right, end-right / start-left, end-right, end-left
  offset = writeVertex(pool.positions, offset, start, sideX, sideZ);
  offset = writeVertex(pool.positions, offset, start, -sideX, -sideZ);
  offset = writeVertex(pool.positions, offset, end, -sideX, -sideZ);
  offset = writeVertex(pool.positions, offset, start, sideX, sideZ);
  offset = writeVertex(pool.positions, offset, end, -sideX, -sideZ);
  writeVertex(pool.positions, offset, end, sideX, sideZ);
  pool.cursor = (pool.cursor + 1) % SKID_SEGMENT_COUNT;
}

const PARTICLE_COLORS = {
  smoke: [0.66, 0.67, 0.68],
  spray: [0.82, 0.88, 0.91],
  grass: [0.6, 0.61, 0.45],
  gravel: [0.71, 0.65, 0.56],
} as const;

function particleColor(state: VehicleState, wetness: number): readonly [number, number, number] {
  if (state.surfaceType === DRIVING_SURFACE_TYPES.PUDDLE || wetness > 0.45) {
    return PARTICLE_COLORS.spray;
  }
  if (state.surfaceType === DRIVING_SURFACE_TYPES.GRASS) {
    return PARTICLE_COLORS.grass;
  }
  if (state.surfaceType === DRIVING_SURFACE_TYPES.GRAVEL) {
    return PARTICLE_COLORS.gravel;
  }
  return PARTICLE_COLORS.smoke;
}

function emitParticle(
  pool: ParticlePool,
  contact: ContactPoint,
  state: VehicleState,
  wetness: number
): void {
  const index = pool.cursor;
  const offset = index * 3;
  const forwardX = Math.cos(state.heading);
  const forwardZ = -Math.sin(state.heading);
  const rightX = Math.sin(state.heading);
  const rightZ = Math.cos(state.heading);
  const velocityX = forwardX * state.speed + rightX * state.lateralSpeed;
  const velocityZ = forwardZ * state.speed + rightZ * state.lateralSpeed;
  const spread = 0.35 + wetness * 0.72;

  pool.positions[offset] = contact.x + (Math.random() - 0.5) * 0.1;
  pool.positions[offset + 1] = contact.y + 0.09;
  pool.positions[offset + 2] = contact.z + (Math.random() - 0.5) * 0.1;
  pool.velocities[offset] = velocityX * 0.1 + (Math.random() - 0.5) * spread;
  pool.velocities[offset + 1] =
    0.34 + Math.random() * 0.42 + wetness * (0.62 + Math.random() * 0.55);
  pool.velocities[offset + 2] = velocityZ * 0.1 + (Math.random() - 0.5) * spread;

  const lifetime = wetness > 0.45
    ? 0.38 + Math.random() * 0.34
    : 0.82 + Math.random() * 0.72;
  pool.life[index] = lifetime;
  pool.maxLife[index] = lifetime;
  pool.baseSize[index] = wetness > 0.45
    ? 0.2 + Math.random() * 0.15
    : 0.42 + Math.random() * 0.28;
  pool.sizes[index] = pool.baseSize[index];
  pool.opacities[index] = 0;
  const color = particleColor(state, wetness);
  pool.colors[offset] = color[0];
  pool.colors[offset + 1] = color[1];
  pool.colors[offset + 2] = color[2];
  pool.cursor = (index + 1) % PARTICLE_COUNT;
}

function updateParticles(pool: ParticlePool, dt: number, wetness: number): void {
  for (let index = 0; index < PARTICLE_COUNT; index += 1) {
    if (pool.life[index] <= 0) {
      continue;
    }
    const offset = index * 3;
    pool.life[index] -= dt;
    if (pool.life[index] <= 0) {
      pool.positions[offset + 1] = PARTICLE_HIDE_Y;
      pool.opacities[index] = 0;
      pool.sizes[index] = 0;
      continue;
    }

    pool.positions[offset] += pool.velocities[offset] * dt;
    pool.positions[offset + 1] += pool.velocities[offset + 1] * dt;
    pool.positions[offset + 2] += pool.velocities[offset + 2] * dt;
    const damping = Math.exp(-(1.45 + wetness * 1.4) * dt);
    pool.velocities[offset] *= damping;
    pool.velocities[offset + 2] *= damping;

    const age = 1 - pool.life[index] / pool.maxLife[index];
    const fadeIn = Math.min(1, age / 0.16);
    const fadeOut = Math.min(1, pool.life[index] / (pool.maxLife[index] * 0.52));
    pool.opacities[index] = fadeIn * fadeOut * (0.25 + wetness * 0.2);
    pool.sizes[index] =
      pool.baseSize[index] * (1 + age * (wetness > 0.45 ? 1.15 : 2.2));
  }
}

export interface TireSlipEffectsProps {
  bounds: CarBounds;
  stateRef: RefObject<VehicleState>;
  /** 날씨 객체(식별자 고정). 젖음은 프레임 루프에서 읽습니다. */
  weather?: RuntimeWeather;
  /** 그래픽 품질의 입자 비율(0~1). 버퍼는 그대로 두고 방출량만 줄입니다. */
  particleRatio?: number;
}

/**
 * 타이어 한계를 넘을 때 소프트 연기/물보라와 노면에 밀착한 실제 폭의 자국을 남깁니다.
 * 모든 버퍼는 고정 크기 순환 풀이라 주행 시간이 길어져도 객체와 draw call이 늘지 않습니다.
 */
export function TireSlipEffects({
  bounds,
  stateRef,
  weather,
  particleRatio = 1,
}: TireSlipEffectsProps) {
  const particlePool = useMemo(createParticlePool, []);
  const skidPool = useMemo(createSkidPool, []);
  const smokeMaterial = useMemo(createSmokeMaterial, []);
  const skidMaterialRef = useRef<MeshBasicMaterial | null>(null);
  const previous = useRef<PreviousContacts>({
    left: null,
    right: null,
    spawnCount: stateRef.current.spawnCount,
  });

  useEffect(
    () => () => {
      particlePool.geometry.dispose();
      skidPool.geometry.dispose();
      smokeMaterial.dispose();
    },
    [particlePool, skidPool, smokeMaterial]
  );

  useFrame((_, frameDelta) => {
    const dt = Math.min(Math.max(frameDelta, 0), EFFECT_STEP_LIMIT);
    const state = stateRef.current;
    // 날씨 젖음은 렌더가 아니라 여기서 읽습니다(전환 중 재렌더를 만들지 않습니다).
    const weatherWetness = weather === undefined ? 0 : Math.min(1, Math.max(0, weather.roadWetness));
    const effectWetness = Math.max(weatherWetness, state.surfaceWetness);
    const offRoad =
      state.surfaceType === DRIVING_SURFACE_TYPES.GRASS ||
      state.surfaceType === DRIVING_SURFACE_TYPES.GRAVEL;
    const inPuddle = state.surfaceType === DRIVING_SURFACE_TYPES.PUDDLE;
    updateParticles(particlePool, dt, effectWetness);

    if (previous.current.spawnCount !== state.spawnCount) {
      previous.current = { left: null, right: null, spawnCount: state.spawnCount };
    }

    const hasEnoughSpeed = Math.abs(state.speed) >= MIN_EFFECT_SPEED;
    const offRoadDust =
      offRoad && hasEnoughSpeed &&
      (Math.abs(state.lateralSpeed) > 0.18 || Math.abs(state.longitudinalAcceleration) > 1.6);
    const shouldEmitSmoke =
      hasEnoughSpeed && (state.instantaneousSlip >= MIN_SMOKE_SLIP || offRoadDust || inPuddle);
    const shouldLeaveSkid =
      state.surfaceType === DRIVING_SURFACE_TYPES.ASPHALT &&
      hasEnoughSpeed &&
      state.instantaneousSlip >= MIN_SKID_SLIP;

    if (!shouldEmitSmoke && !shouldLeaveSkid) {
      previous.current.left = null;
      previous.current.right = null;
      particlePool.emissionRemainder = 0;
    } else {
      const left = tyreContact(state, bounds.rearWheelX, -bounds.wheelHalfTrack);
      const right = tyreContact(state, bounds.rearWheelX, bounds.wheelHalfTrack);

      if (shouldEmitSmoke) {
        const effectStrength = Math.max(
          state.instantaneousSlip,
          offRoadDust ? 0.32 : 0,
          inPuddle ? 0.55 : 0
        );
        const wetEmissionMultiplier = 1 + effectWetness * 0.45;
        particlePool.emissionRemainder +=
          effectStrength *
          PARTICLES_PER_SECOND *
          wetEmissionMultiplier *
          Math.min(1, Math.max(0.2, particleRatio)) *
          dt;
        let emitLeft = particlePool.cursor % 2 === 0;
        while (particlePool.emissionRemainder >= 1) {
          emitParticle(particlePool, emitLeft ? left : right, state, effectWetness);
          emitLeft = !emitLeft;
          particlePool.emissionRemainder -= 1;
        }
      } else {
        particlePool.emissionRemainder = 0;
      }

      if (shouldLeaveSkid) {
        if (previous.current.left !== null && previous.current.right !== null) {
          addSkidSegment(skidPool, previous.current.left, left);
          addSkidSegment(skidPool, previous.current.right, right);
        }
        previous.current.left = left;
        previous.current.right = right;
      } else {
        previous.current.left = null;
        previous.current.right = null;
      }
    }

    (particlePool.geometry.getAttribute('position') as BufferAttribute).needsUpdate = true;
    (particlePool.geometry.getAttribute('aSize') as BufferAttribute).needsUpdate = true;
    (particlePool.geometry.getAttribute('aOpacity') as BufferAttribute).needsUpdate = true;
    (particlePool.geometry.getAttribute('aColor') as BufferAttribute).needsUpdate = true;
    (skidPool.geometry.getAttribute('position') as BufferAttribute).needsUpdate = true;

    // 자국 색은 젖은 노면에서 옅어집니다. 재질 값만 갱신해 재렌더를 피합니다.
    if (skidMaterialRef.current !== null) {
      skidMaterialRef.current.opacity = 0.58 * (1 - effectWetness * 0.42);
    }
  });

  return (
    <>
      <points
        geometry={particlePool.geometry}
        material={smokeMaterial}
        frustumCulled={false}
        renderOrder={12}
      />
      <mesh geometry={skidPool.geometry} frustumCulled={false} renderOrder={4}>
        <meshBasicMaterial
          ref={skidMaterialRef}
          color="#080a0b"
          transparent
          opacity={0.58}
          depthWrite={false}
          polygonOffset
          polygonOffsetFactor={-2}
          polygonOffsetUnits={-2}
        />
      </mesh>
    </>
  );
}
